// client/src/components/chat/ChannelList.js
import { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { formatChannelName, formatChannelPreview } from '../../utils/chatUtils'

function ChannelList({
  channels,
  activeChannel,
  onChannelSelect,
  loading,
  getOnlineCountForChannel,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(0)
  const channelRefs = useRef([])
  const listRef = useRef(null)

  // Filter channels based on search query
  const filteredChannels = useMemo(() => {
    if (!searchQuery.trim()) {
      return channels || []
    }

    const query = searchQuery.toLowerCase()
    return (channels || []).filter((channel) => {
      const channelName = formatChannelName(channel).toLowerCase()
      const channelPreview = formatChannelPreview(channel).toLowerCase()
      return channelName.includes(query) || channelPreview.includes(query)
    })
  }, [channels, searchQuery])

  // Reset focused index when filtered channels change
  useEffect(() => {
    // When there are no channels, there is nothing to focus
    if (filteredChannels.length === 0) {
      if (focusedIndex !== -1) {
        setFocusedIndex(-1)
      }
      return
    }

    // Clamp focused index to valid range when channels exist
    if (focusedIndex < 0) {
      setFocusedIndex(0)
    } else if (focusedIndex >= filteredChannels.length) {
      setFocusedIndex(filteredChannels.length - 1)
    }
  }, [filteredChannels.length, focusedIndex])

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e) => {
      if (filteredChannels.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex((prev) =>
            prev < filteredChannels.length - 1 ? prev + 1 : prev,
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (filteredChannels[focusedIndex]) {
            onChannelSelect(filteredChannels[focusedIndex])
          }
          break
        case 'Home':
          e.preventDefault()
          setFocusedIndex(0)
          break
        case 'End':
          e.preventDefault()
          setFocusedIndex(filteredChannels.length - 1)
          break
        default:
          break
      }
    },
    [filteredChannels, focusedIndex, onChannelSelect],
  )

  // Focus management - update focus when focusedIndex or channel list changes
  useEffect(() => {
    const el = channelRefs.current[focusedIndex]
    if (el && typeof el.focus === 'function') {
      el.focus()
    }
  }, [focusedIndex, filteredChannels.length])

  // Handle channel click
  const handleChannelClick = useCallback(
    (channel, index) => {
      setFocusedIndex(index)
      onChannelSelect(channel)
    },
    [onChannelSelect],
  )
  if (loading) {
    return (
      <div className="chat-sidebar">
        <div className="channel-list-header">
          <h3 className="channel-list-title">Channels</h3>
        </div>
        <div className="empty-state" role="status" aria-live="polite">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading channels...</p>
        </div>
      </div>
    )
  }

  if (!channels || channels.length === 0) {
    return (
      <div className="chat-sidebar">
        <div className="channel-list-header">
          <h3 className="channel-list-title">Channels</h3>
        </div>
        <div className="empty-state">
          <div className="empty-state-icon">💬</div>
          <h4 className="empty-state-title">Chat Coming Soon</h4>
          <p className="empty-state-text">
            Chat opens when your division is full. Check back soon!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-sidebar">
      <div className="channel-list-header">
        <h3 className="channel-list-title" id="channel-list-label">
          Channels
        </h3>
        {/* Search input for filtering channels */}
        <div className="channel-search">
          <input
            type="text"
            placeholder="Search channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="channel-search-input"
            aria-label="Search channels"
          />
        </div>
      </div>
      <div
        className="channel-list"
        role="listbox"
        aria-labelledby="channel-list-label"
        aria-activedescendant={
          filteredChannels.length > 0 &&
          focusedIndex >= 0 &&
          focusedIndex < filteredChannels.length
            ? `channel-${filteredChannels[focusedIndex].id}`
            : undefined
        }
        tabIndex={0}
        onKeyDown={handleKeyDown}
        ref={listRef}
      >
        {filteredChannels.map((channel, index) => {
          const onlineCount = getOnlineCountForChannel
            ? getOnlineCountForChannel(channel.id)
            : 0

          const name = formatChannelName(channel)
          const initials =
            name
              .split(' ')
              .filter((n) => n.length > 0)
              .map((n) => n[0])
              .slice(0, 2)
              .join('')
              .toUpperCase() || '?'

          const isActive = activeChannel?.id === channel.id
          const isFocused = index === focusedIndex

          return (
            <div
              key={channel.id}
              id={`channel-${channel.id}`}
              data-channel-id={channel.id}
              className={`channel-item ${isActive ? 'active' : ''}`}
              onClick={() => handleChannelClick(channel, index)}
              role="option"
              aria-selected={isActive}
              tabIndex={isFocused ? 0 : -1}
              ref={(el) => (channelRefs.current[index] = el)}
              aria-label={`${name}, ${formatChannelPreview(channel)}${
                onlineCount > 0 ? `, ${onlineCount} online` : ''
              }`}
            >
              <div className="channel-avatar" aria-hidden="true">
                {initials}
              </div>
              <div className="channel-info">
                <div className="channel-header">
                  <div className="channel-name">{name}</div>
                  {onlineCount > 0 && (
                    <div
                      className="online-count"
                      aria-label={`${onlineCount} online`}
                    >
                      <span className="online-dot" aria-hidden="true"></span>
                      <span aria-hidden="true">{onlineCount}</span>
                    </div>
                  )}
                </div>
                <div className="channel-preview" aria-hidden="true">
                  {formatChannelPreview(channel)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders
export default memo(ChannelList, (prevProps, nextProps) => {
  return (
    prevProps.channels === nextProps.channels &&
    prevProps.activeChannel?.id === nextProps.activeChannel?.id &&
    prevProps.loading === nextProps.loading &&
    prevProps.onChannelSelect === nextProps.onChannelSelect &&
    prevProps.getOnlineCountForChannel === nextProps.getOnlineCountForChannel
  )
})
// Note: searchQuery is internal state, so we don't need to compare it in the memo function
